import { useEffect, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { collection, query, orderBy, limit as fsLimit, onSnapshot, doc as fsDoc, where, type Timestamp } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { db, functions } from '@/lib/firebase'
import { toast } from 'sonner'

export type BulkKind = 'valuation' | 'valuation_doc' | 'whitepaper' | 'marketing_deck' | 'improvement_audit'

export interface BulkJob {
  id: string
  type: 'bulk_valuate' | 'bulk_generate'
  status: 'queued' | 'running' | 'completed' | 'cancelled' | 'failed'
  kinds: BulkKind[]
  total: number
  pending: number
  running: number
  succeeded: number
  failed: number
  created_at?: Timestamp
  started_at?: Timestamp
  completed_at?: Timestamp
  created_by: string
}

export interface BulkJobItem {
  id: string
  app_slug: string
  app_name: string
  kind: BulkKind
  status: 'pending' | 'running' | 'done' | 'failed' | 'cancelled'
  error?: string
  started_at?: string
  completed_at?: string
}

/** Live snapshot of recent bulk jobs (active first, then completed). */
export function useBulkJobs(limit = 25) {
  const [jobs, setJobs] = useState<BulkJob[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const q = query(collection(db, 'bulk_jobs'), orderBy('created_at', 'desc'), fsLimit(limit))
    const unsub = onSnapshot(q, (snap) => {
      setJobs(snap.docs.map(d => ({ id: d.id, ...(d.data() as Omit<BulkJob, 'id'>) })))
      setLoading(false)
    }, () => setLoading(false))
    return () => unsub()
  }, [limit])

  const active = jobs.filter(j => j.status === 'queued' || j.status === 'running')
  const recent = jobs.filter(j => j.status !== 'queued' && j.status !== 'running')
  return { jobs, active, recent, loading }
}

/** Live snapshot of items inside a single job. */
export function useBulkJobItems(jobId: string | null) {
  const [items, setItems] = useState<BulkJobItem[]>([])
  useEffect(() => {
    // Clear stale items on every job transition, not only when jobId is null.
    setItems([])
    if (!jobId) return
    const q = collection(db, 'bulk_jobs', jobId, 'items')
    const unsub = onSnapshot(q, snap => {
      setItems(snap.docs.map(d => ({ id: d.id, ...(d.data() as Omit<BulkJobItem, 'id'>) })))
    })
    return () => unsub()
  }, [jobId])
  return items
}

/** Recent failed items across all jobs — useful for retry UI. */
export function useFailedItems(jobId: string | null) {
  const [items, setItems] = useState<BulkJobItem[]>([])
  useEffect(() => {
    // Clear stale items on every job transition, not only when jobId is null.
    setItems([])
    if (!jobId) return
    const q = query(collection(db, 'bulk_jobs', jobId, 'items'), where('status', '==', 'failed'))
    const unsub = onSnapshot(q, snap => {
      setItems(snap.docs.map(d => ({ id: d.id, ...(d.data() as Omit<BulkJobItem, 'id'>) })))
    })
    return () => unsub()
  }, [jobId])
  return items
}

/** Single job snapshot (live). */
export function useBulkJob(jobId: string | null) {
  const [job, setJob] = useState<BulkJob | null>(null)
  useEffect(() => {
    if (!jobId) { setJob(null); return }
    const unsub = onSnapshot(fsDoc(db, 'bulk_jobs', jobId), snap => {
      setJob(snap.exists() ? ({ id: snap.id, ...(snap.data() as Omit<BulkJob, 'id'>) }) : null)
    })
    return () => unsub()
  }, [jobId])
  return job
}

/* Mutations */

export function useBulkValuate() {
  const fn = httpsCallable<{ force?: boolean }, { ok: boolean; jobId: string | null; processed?: number; message?: string }>(
    functions, 'bulkValuate'
  )
  return useMutation({
    mutationFn: async (force?: boolean) => (await fn({ force: force ?? false })).data,
    onSuccess: (r) => {
      if (r.message) toast.info(r.message)
      else if (r.processed) toast.success(`Bulk valuate complete · ${r.processed} apps processed`)
    },
    onError: (e: Error) => toast.error(e.message),
  })
}

export function useEnqueueBulkGenerate() {
  const fn = httpsCallable<
    { kinds?: BulkKind[]; slugs?: string[]; force?: boolean },
    { ok: boolean; jobId: string | null; queued?: number; message?: string }
  >(functions, 'enqueueBulkGenerate')
  return useMutation({
    mutationFn: async (input: { kinds?: BulkKind[]; slugs?: string[]; force?: boolean }) => (await fn(input)).data,
    onSuccess: (r) => {
      if (r.message) toast.info(r.message)
      else if (r.queued) toast.success(`Queued · ${r.queued} items will process every minute`)
    },
    onError: (e: Error) => toast.error(e.message),
  })
}

export function useBulkWorkerNow() {
  const fn = httpsCallable<unknown, { ok: boolean; runs: number }>(functions, 'bulkWorkerNow')
  return useMutation({
    mutationFn: async () => (await fn()).data,
    onSuccess: (r) => toast.success(`Worker ran · ${r.runs} items processed`),
    onError: (e: Error) => toast.error(e.message),
  })
}

export function useCancelBulkJob() {
  const fn = httpsCallable<{ jobId: string }, { ok: boolean; cancelled_items: number }>(functions, 'cancelBulkJob')
  return useMutation({
    mutationFn: async (jobId: string) => (await fn({ jobId })).data,
    onSuccess: (r) => toast.success(`Cancelled · ${r.cancelled_items} pending items dropped`),
    onError: (e: Error) => toast.error(e.message),
  })
}
